import React, { useEffect, useRef } from 'react';
import { Checkbox, Table } from 'antd';
import type { ColumnsType, ColumnType, TableProps } from 'antd/es/table';
import { Pager } from '@/components/Pager';
import { ChevronRight, Flag } from 'lucide-react';
import { CardMenuButton } from '@/components/CardMenu';
import { useBreakpoint, useMediaQuery } from '@/hooks';
import { mediaUp } from '@/styles/breakpoints';
import { useHorizontalScrollbar } from './useHorizontalScrollbar';
import styles from './DataTable.module.scss';

const isLeafColumn = <T,>(col: ColumnsType<T>[number]): col is ColumnType<T> => !('children' in col);

const renderCellContent = <T,>(col: ColumnType<T>, value: unknown, record: T, index: number): React.ReactNode => {
  const result = col.render ? col.render(value, record, index) : (value as React.ReactNode);
  if (result && typeof result === 'object' && 'children' in result) {
    return (result as { children: React.ReactNode }).children;
  }
  return result as React.ReactNode;
};

export type DataTableRowTone = 'success' | 'warning' | 'danger' | 'muted';

export interface DataTableMobileRow {
  title: string;
  flagged?: boolean;
  subtitle?: string;
  statusLabel?: string;
  statusTone?: DataTableRowTone;
  date?: string;
  scoreValue?: string;
  scoreLabel?: string;
  scoreTone?: DataTableRowTone;
  onOpen?: () => void;
}

const TONE_CLASS: Record<DataTableRowTone, string> = {
  success: styles.toneSuccess,
  warning: styles.toneWarning,
  danger: styles.toneDanger,
  muted: styles.toneMuted,
};

const toneClass = (tone?: DataTableRowTone): string => (tone ? TONE_CLASS[tone] : '');

interface DataTableProps<T extends object> {
  dataSource: T[];
  columns: ColumnsType<T>;
  rowKey: string;
  loading: boolean;
  page: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onChange?: TableProps<T>['onChange'];
  onRow?: TableProps<T>['onRow'];
  rowClassName?: TableProps<T>['rowClassName'];
  expandable?: TableProps<T>['expandable'];
  locale?: TableProps<T>['locale'];
  rowSelection?: TableProps<T>['rowSelection'];
  onMobileDetails?: (record: T) => void;
  mobileActionsLabel?: (record: T) => string;
  mobileTitle?: (record: T) => React.ReactNode;
  mobileRow?: (record: T) => DataTableMobileRow;
}

// antd writes `scroll.x` straight onto the table as an inline width. With
// `'max-content'` that width is the content's max-content size, which for these
// tables runs wider than the sum of the columns' declared widths — and because
// the layout is `table-layout: fixed`, that surplus gets distributed across the
// columns, so a declared `width` is never actually binding (Package declared
// 200px and rendered 454px). Passing the sum as a number instead makes the
// declared widths authoritative and lets `ellipsis` do its job.
//
// Only safe when every column declares a numeric width; otherwise the missing
// columns would collapse, so fall back to the previous behaviour. That's the
// case warnAboutUnsizedEllipsisColumns below already flags in dev.
function totalColumnWidth<T extends object>(columns: ColumnsType<T>): number | 'max-content' {
  let total = 0;
  for (const column of columns) {
    if (typeof column.width !== 'number') return 'max-content';
    total += column.width;
  }
  return total || 'max-content';
}

const warnedColumnKeys = new Set<string>();

function warnAboutUnsizedEllipsisColumns<T extends object>(columns: ColumnsType<T>) {
  columns.forEach((column) => {
    if (!('ellipsis' in column) || !column.ellipsis || 'width' in column) return;
    const identifier = String('key' in column ? column.key : 'dataIndex' in column ? column.dataIndex : column.title);
    if (warnedColumnKeys.has(identifier)) return;
    warnedColumnKeys.add(identifier);
    console.warn(
      `[DataTable] Column "${identifier}" has "ellipsis: true" but no explicit "width" — this breaks horizontal-scroll sizing. Add a width.`,
    );
  });
}

export function DataTable<T extends object>({
  dataSource,
  columns,
  rowKey,
  loading,
  page,
  totalCount,
  pageSize,
  onPageChange,
  onChange,
  onRow,
  rowClassName,
  expandable,
  locale,
  rowSelection,
  onMobileDetails,
  mobileActionsLabel,
  mobileTitle,
  mobileRow,
}: DataTableProps<T>) {
  const { isMobile } = useBreakpoint();

  // Pager takes a 0-based page; this component's own `page`/`onPageChange`
  // stay 1-based to match every other server-paginated call site.
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));
  const from = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalCount);

  useEffect(() => {
    if (import.meta.env.DEV) warnAboutUnsizedEllipsisColumns(columns);
  }, [columns]);

  const scrollX = totalColumnWidth(columns);
  const isTabletUp = useMediaQuery(mediaUp('lg'));
  const wrapRef = useRef<HTMLDivElement>(null);
  const {
    trackRef,
    needsScrollbar,
    thumbWidthRatio,
    thumbLeftRatio,
    handleTrackPointerDown,
    handleThumbPointerDown,
  } = useHorizontalScrollbar(wrapRef, '.ant-table-content', !isMobile);

  const flattenRows = (rows: T[]): T[] =>
    rows.flatMap((row) => {
      const children = (row as { children?: T[] }).children;
      return children?.length ? [row, ...flattenRows(children)] : [row];
    });
  const mobileRows = flattenRows(dataSource);
  const keyOf = (record: T): React.Key => (record as Record<string, unknown>)[rowKey] as React.Key;

  // The desktop table hands rowSelection to antd; the mobile row list has to
  // honour the same controlled contract itself so a page's selection state
  // (e.g. batch export) is shared across breakpoints.
  const selectedKeys = rowSelection?.selectedRowKeys ?? [];
  const selectableKeys = rowSelection
    ? mobileRows.filter((r) => !rowSelection.getCheckboxProps?.(r)?.disabled).map(keyOf)
    : [];
  const selectedOnPage = selectableKeys.filter((k) => selectedKeys.includes(k)).length;

  const emitMobileSelection = (keys: React.Key[], type: 'single' | 'all' | 'none') =>
    rowSelection?.onChange?.(keys, mobileRows.filter((r) => keys.includes(keyOf(r))), { type });

  const toggleMobileSelection = (record: T, checked: boolean) => {
    const key = keyOf(record);
    emitMobileSelection(checked ? [...selectedKeys, key] : selectedKeys.filter((k) => k !== key), 'single');
  };

  // Like antd's header checkbox: acts on this page's selectable rows only, and
  // leaves selections carried over from other pages alone.
  const toggleMobileSelectAll = (checked: boolean) => {
    const otherKeys = selectedKeys.filter((k) => !selectableKeys.includes(k));
    emitMobileSelection(checked ? [...otherKeys, ...selectableKeys] : otherKeys, checked ? 'all' : 'none');
  };

  const leafColumns = columns.filter(isLeafColumn);
  const actionsCol = leafColumns.find((col) => col.key === 'actions');
  const allFieldCols = leafColumns.filter((col) => col.key !== 'actions');
  const [titleCol, ...restCols] = allFieldCols;
  const fieldCols = onMobileDetails ? restCols : allFieldCols;

  return (
    <div>
      {isMobile && mobileRow ? (
        <div className={styles.rowList}>
          {loading && <div className={styles.mobileEmpty}>Loading…</div>}
          {!loading && rowSelection && mobileRows.length > 0 && (
            <div className={styles.rowSelectAll}>
              <Checkbox
                checked={selectableKeys.length > 0 && selectedOnPage === selectableKeys.length}
                indeterminate={selectedOnPage > 0 && selectedOnPage < selectableKeys.length}
                disabled={selectableKeys.length === 0}
                onChange={(e) => toggleMobileSelectAll(e.target.checked)}
              >
                Select all
              </Checkbox>
            </div>
          )}
          {!loading && mobileRows.length === 0 && (
            <div className={styles.mobileEmpty}>
              {typeof locale?.emptyText === 'function' ? locale.emptyText() : (locale?.emptyText ?? 'No data')}
            </div>
          )}
          {!loading &&
            mobileRows.map((record) => {
              const row = mobileRow(record);
              const body = (
                <>
                  <div className={styles.rowText}>
                    <div className={styles.rowTitle}>
                      <span className={styles.rowTitleText}>{row.title}</span>
                      {row.flagged && (
                        <Flag
                          className={styles.rowFlag}
                          aria-label="Flagged"
                          role="img"
                          style={{ width: '1em', height: '1em' }}
                        />
                      )}
                    </div>
                    {row.subtitle && <div className={styles.rowSubtitle}>{row.subtitle}</div>}
                    {(row.statusLabel || row.date) && (
                      <div className={styles.rowMeta}>
                        {row.statusLabel && (
                          <span className={`${styles.rowStatus} ${toneClass(row.statusTone)}`}>
                            <span className={styles.rowDot} aria-hidden="true" />
                            {row.statusLabel}
                          </span>
                        )}
                        {row.statusLabel && row.date && <span aria-hidden="true">·</span>}
                        {row.date && <span>{row.date}</span>}
                      </div>
                    )}
                  </div>
                  <div className={`${styles.rowScore} ${toneClass(row.scoreTone)}`}>
                    <span className={styles.rowScoreValue}>{row.scoreValue ?? '—'}</span>
                    {row.scoreLabel && <span className={styles.rowScoreLabel}>{row.scoreLabel}</span>}
                  </div>
                  {row.onOpen ? (
                    <ChevronRight className={styles.rowChevron} style={{ width: '1em', height: '1em' }} aria-hidden="true" />
                  ) : (
                    <span className={styles.rowChevronSpacer} aria-hidden="true" />
                  )}
                </>
              );
              const key = String(keyOf(record));
              const rowElement = row.onOpen ? (
                <button
                  key={key}
                  type="button"
                  className={`${styles.row} ${styles.rowLink}`}
                  onClick={row.onOpen}
                  aria-label={`View results for ${row.title}${row.subtitle ? `, ${row.subtitle}` : ''}`}
                >
                  {body}
                </button>
              ) : (
                <div key={key} className={styles.row}>
                  {body}
                </div>
              );
              if (!rowSelection) return rowElement;
              // A sibling of the row, not a child — a checkbox can't nest inside the row's <button>.
              return (
                <div key={key} className={styles.rowSelectable}>
                  <Checkbox
                    className={styles.rowCheckbox}
                    checked={selectedKeys.includes(keyOf(record))}
                    disabled={rowSelection.getCheckboxProps?.(record)?.disabled}
                    onChange={(e) => toggleMobileSelection(record, e.target.checked)}
                    aria-label={`Select ${row.title}`}
                  />
                  {rowElement}
                </div>
              );
            })}
        </div>
      ) : isMobile ? (
        <div className={styles.mobileList}>
          {loading && <div className={styles.mobileEmpty}>Loading…</div>}
          {!loading && mobileRows.length === 0 && (
            <div className={styles.mobileEmpty}>
              {typeof locale?.emptyText === 'function' ? locale.emptyText() : (locale?.emptyText ?? 'No data')}
            </div>
          )}
          {!loading &&
            mobileRows.map((record, index) => (
              <div className={styles.mobileCard} key={String((record as Record<string, unknown>)[rowKey])}>
                {onMobileDetails && (
                  <div className={styles.mobileHead}>
                    <div className={styles.mobileTitle}>
                      {mobileTitle
                        ? mobileTitle(record)
                        : titleCol &&
                          renderCellContent(
                            titleCol,
                            typeof titleCol.dataIndex === 'string'
                              ? (record as Record<string, unknown>)[titleCol.dataIndex]
                              : undefined,
                            record,
                            index
                          )}
                    </div>
                    <div className={styles.mobileMenu}>
                      <CardMenuButton
                        haspopup="dialog"
                        ariaLabel={mobileActionsLabel ? mobileActionsLabel(record) : 'More actions'}
                        onClick={() => onMobileDetails(record)}
                      />
                    </div>
                  </div>
                )}
                <div className={styles.mobileFields}>
                  {fieldCols.map((col) => {
                    const value = typeof col.dataIndex === 'string'
                      ? (record as Record<string, unknown>)[col.dataIndex]
                      : undefined;
                    return (
                      <div className={styles.mobileField} key={String(col.key)}>
                        <span className={styles.mobileFieldLabel}>{col.title as React.ReactNode}</span>
                        <span className={styles.mobileFieldValue}>
                          {renderCellContent(col, value, record, index)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                {!onMobileDetails && actionsCol && (
                  <div className={styles.mobileActions}>
                    {renderCellContent(actionsCol, undefined, record, index)}
                  </div>
                )}
              </div>
            ))}
        </div>
      ) : (
        <>
          <div className={styles.tableWrap} ref={wrapRef}>
            <Table<T>
              dataSource={dataSource}
              columns={columns}
              rowKey={rowKey}
              loading={loading}
              pagination={false}
              scroll={{ x: scrollX }}
              styles={{ content: { overflowX: 'scroll' } }}
              onChange={onChange}
              onRow={onRow}
              rowClassName={rowClassName}
              expandable={expandable}
              locale={locale}
              rowSelection={rowSelection}
            />
          </div>
          {isTabletUp && needsScrollbar && (
            <div className={styles.scrollbarTrack} ref={trackRef} onPointerDown={handleTrackPointerDown}>
              <div
                className={styles.scrollbarThumb}
                style={{ width: `${thumbWidthRatio * 100}%`, left: `${thumbLeftRatio * 100}%` }}
                onPointerDown={handleThumbPointerDown}
              />
            </div>
          )}
        </>
      )}
      <Pager
        page={page - 1}
        pageCount={pageCount}
        from={from}
        to={to}
        total={totalCount}
        onChange={(zeroBasedPage) => onPageChange(zeroBasedPage + 1)}
      />
    </div>
  );
}
